import { practiceTypeOptionName } from "constants/practiceType";
import {
  hasSecondaryBenefits,
  resolveOrderBenefits,
  resolveOrderSecondaryBenefits,
} from "helpers/orderBenefits";
import { centsToDollars, isNoMaximumOOPDollars } from "helpers/price";
import { Label } from "layout/typography/Label";
import { EngineProvenanceBanner } from "pages/CreateOrder/components/PatientBenefitsSection/components/Benefits/components/EngineProvenanceBanner";
import { PatientBenefits as PatientBenefitsDetails } from "pages/CreateOrder/components/PatientBenefitsSection/components/Benefits/components/PatientBenefits";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { TabPanel, TabView } from "primereact/tabview";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { TourAnchor } from "training/anchors";
import { IOrder } from "types/Order/Order";

import { SectionHeader } from "../SectionParts";

const StyledInputNumber = styled(InputNumber)`
  width: 100%;
  input {
    width: 100%;
  }
`;

const StyledDialogContent = styled.div`
  /* Ensure content fills the dialog */
  width: 100%;
  overflow-x: hidden;

  /* Hide the unnecessary vertical scrollbar on the inner tab nav */
  .p-tabview-nav {
    overflow-y: hidden !important;
  }
`;

interface IPatientBenefits {
  deductible: number;
  outOfPocketMax: number;
  flatCopay: number;
  coinsurancePercent: number;
  deductible2: number;
  outOfPocketMax2: number;
  flatCopay2: number;
  coinsurancePercent2: number;
  payerName: string;
  payerName2: string;
  placeOfService: string;
  locality: string;
  memberId: string;
  memberId2: string;
  serviceType: string;
  serviceType2: string;
}

function PatientBenefits({ order }: { order: IOrder }) {
  const { control, setValue } = useFormContext<IPatientBenefits>();
  const payerName2Field = (order.serviceDetails as any)?.secondaryPayerName;
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const hasBenefitDetails = !!order?.benefitDetails;
  const hasSecondaryBenefitDetails = !!order?.secondaryBenefitDetails;
  const hasSecondaryInsurance = !!order?.secondaryPayer;

  useEffect(() => {
    setValue("payerName", (order.serviceDetails as any)?.payerName || "Any");
    setValue(
      "payerName2",
      (order.serviceDetails as any)?.secondaryPayerName || "Any"
    );
    // CLE-2635 — Athena orders can have no quote but real eligibility values.
    const benefits = resolveOrderBenefits(order);
    setValue("deductible", centsToDollars(benefits.remainingDeductibleInCents));
    setValue("outOfPocketMax", centsToDollars(benefits.outOfPocketMaxInCents));
    setValue("flatCopay", centsToDollars(benefits.flatCopayInCents));
    setValue("coinsurancePercent", benefits.coinsurancePercent);
    setValue(
      "memberId",
      order?.benefitDetails?.request?.member?.memberID ||
        order?.patient?.patientBenefits?.memberID ||
        null
    );
    // CLE-3088 — through the legacy alias, so a stored 28 shows "ASC Facility", not the fallback.
    setValue(
      "serviceType",
      practiceTypeOptionName(
        order?.benefitDetails?.request?.practiceTypeCode ||
          order?.serviceDetails?.practiceTypeCode
      ) ?? "All Other Medical"
    );

    // Set secondary insurance values
    if (hasSecondaryBenefits(order)) {
      const secondary = resolveOrderSecondaryBenefits(order);
      setValue(
        "memberId2",
        order?.secondaryBenefitDetails?.request?.member?.memberID ||
          order?.patient?.secondaryPatientBenefits?.memberID ||
          null
      );
      setValue(
        "serviceType2",
        practiceTypeOptionName(
          order?.secondaryBenefitDetails?.request?.practiceTypeCode ||
            order?.serviceDetails?.secondaryPracticeTypeCode
        ) ?? "All Other Medical"
      );
      setValue(
        "deductible2",
        centsToDollars(secondary.remainingDeductibleInCents)
      );
      setValue(
        "outOfPocketMax2",
        centsToDollars(secondary.outOfPocketMaxInCents)
      );
      setValue("flatCopay2", centsToDollars(secondary.flatCopayInCents));
      setValue("coinsurancePercent2", secondary.coinsurancePercent);
    }
  }, [order, setValue]);

  return (
    <section
      className="px-3 pb-3 border-bottom-1"
      data-tour={TourAnchor.PatientBenefits}
      style={{
        borderColor: "var(--brand-gray-200)",
        marginLeft: "-1rem",
        marginRight: "-1rem",
      }}
    >
      <SectionHeader icon="pi pi-shield" title="Insurance & Benefits">
        {hasBenefitDetails && (
          <Button
            className="p-button-text p-button-plain p-button-sm"
            onClick={() => setShowDetailsDialog(true)}
          >
            See Details
          </Button>
        )}
      </SectionHeader>
      <Dialog
        header="Eligibility Benefits Details"
        visible={showDetailsDialog}
        onHide={() => setShowDetailsDialog(false)}
        style={{ width: "900px", maxWidth: "90vw" }}
        dismissableMask
        blockScroll
      >
        <StyledDialogContent>
          {hasSecondaryBenefitDetails ? (
            <TabView>
              <TabPanel header="Primary Benefits">
                <PatientBenefitsDetails
                  benefitDetailsField={order.benefitDetails}
                  readOnly
                  fullWidth
                />
              </TabPanel>
              <TabPanel header="Secondary Benefits">
                <PatientBenefitsDetails
                  benefitDetailsField={order.secondaryBenefitDetails}
                  readOnly
                  fullWidth
                />
              </TabPanel>
            </TabView>
          ) : (
            <PatientBenefitsDetails
              benefitDetailsField={order.benefitDetails}
              readOnly
              fullWidth
            />
          )}
        </StyledDialogContent>
      </Dialog>
      <div className="formgrid grid w-100 mt-3">
        {hasSecondaryInsurance && (
          <div className="col-12 mb-3">
            <span className="text-base font-bold text-700">
              Primary Insurance
            </span>
          </div>
        )}
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="payerName">Payer Name</Label>

          <Controller
            name="payerName"
            control={control}
            render={({ field: { value } }) => (
              <InputText
                disabled
                data-testid="payerName"
                id="payerName"
                placeholder="Payer Name"
                value={value}
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="serviceType">Service Type</Label>

          <Controller
            name="serviceType"
            control={control}
            render={({ field: { value } }) => (
              <InputText
                disabled
                data-testid="serviceType"
                id="serviceType"
                placeholder="Service Type"
                value={value}
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="memberId">Member ID</Label>

          <Controller
            name="memberId"
            control={control}
            render={({ field: { value } }) => (
              <InputText
                disabled
                data-testid="memberId"
                id="memberId"
                placeholder="Member ID"
                value={value}
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-0 lg:col-3" />
        <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
          <Label htmlFor="deductible">Remaining Deductible</Label>

          <Controller
            name="deductible"
            control={control}
            render={({ field: { value } }) => (
              <StyledInputNumber
                disabled
                data-testid="deductible"
                id="deductible"
                placeholder="Remaining Deductible"
                value={value}
                currency="USD"
                locale="en-US"
                mode="currency"
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3">
          <Label htmlFor="outOfPocketMax">Remaining Out-of-pocket Max</Label>

          <Controller
            name="outOfPocketMax"
            control={control}
            render={({ field: { value } }) => (
              <StyledInputNumber
                disabled
                data-testid="outOfPocketMax"
                id="outOfPocketMax"
                placeholder={
                  isNoMaximumOOPDollars(value)
                    ? "No maximum"
                    : "Out of Pocket Max"
                }
                value={isNoMaximumOOPDollars(value) ? null : value}
                currency="USD"
                locale="en-US"
                mode="currency"
              />
            )}
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
          <Label htmlFor="accountId">Flat Copay</Label>

          <Controller
            name="flatCopay"
            control={control}
            render={({ field: { value } }) => (
              <StyledInputNumber
                disabled
                data-testid="flatCopay"
                id="flatCopay"
                placeholder="Flat copay"
                value={value}
                currency="USD"
                locale="en-US"
                mode="currency"
              />
            )}
          />
          {/* CLE-2816 / CLE-2951 — the same yellow notes as the create / redo / copy panel. */}
          <EngineProvenanceBanner
            response={order?.benefitDetails?.response}
            benefit="FLAT_COPAY"
            testId="orderView-engineProvenance-flatCopay"
          />
        </div>
        <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
          <Label htmlFor="coinsurancePercent">Coinsurance Percent</Label>

          <Controller
            name="coinsurancePercent"
            control={control}
            render={({ field: { value } }) => (
              <StyledInputNumber
                disabled
                suffix="%"
                data-testid="coinsurancePercent"
                id="coinsurancePercent"
                placeholder="Coinsurance Percent"
                value={value}
              />
            )}
          />
          <EngineProvenanceBanner
            response={order?.benefitDetails?.response}
            benefit="COINSURANCE_PERCENT"
            testId="orderView-engineProvenance-coinsurancePercent"
          />
        </div>

        {hasSecondaryInsurance && (
          <>
            <div className="col-12 mt-3 mb-3">
              <span className="text-base font-bold text-700">
                Secondary Insurance
              </span>
            </div>
            {payerName2Field && (
              <div className="field flex flex-column col-12 md:col-4 lg:col-3">
                <Label htmlFor="payerName2">Secondary Payer</Label>

                <Controller
                  name="payerName2"
                  control={control}
                  render={({ field: { value } }) => (
                    <InputText
                      disabled
                      data-testid="payerName2"
                      id="payerName2"
                      placeholder="Secondary Payer"
                      value={value}
                    />
                  )}
                />
              </div>
            )}
            <div className="field flex flex-column col-12 md:col-4 lg:col-3">
              <Label htmlFor="serviceType2">Service Type</Label>

              <Controller
                name="serviceType2"
                control={control}
                render={({ field: { value } }) => (
                  <InputText
                    disabled
                    data-testid="serviceType2"
                    id="serviceType2"
                    placeholder="Service Type"
                    value={value}
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3">
              <Label htmlFor="memberId2">Member ID</Label>

              <Controller
                name="memberId2"
                control={control}
                render={({ field: { value } }) => (
                  <InputText
                    disabled
                    data-testid="memberId2"
                    id="memberId2"
                    placeholder="Member ID"
                    value={value}
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-0 lg:col-3" />
            <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
              <Label htmlFor="deductible2">Remaining Deductible</Label>

              <Controller
                name="deductible2"
                control={control}
                render={({ field: { value } }) => (
                  <StyledInputNumber
                    disabled
                    data-testid="deductible2"
                    id="deductible2"
                    placeholder="Remaining Deductible"
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
              <Label htmlFor="outOfPocketMax2">
                Remaining Out-of-pocket Max
              </Label>

              <Controller
                name="outOfPocketMax2"
                control={control}
                render={({ field: { value } }) => (
                  <StyledInputNumber
                    disabled
                    data-testid="outOfPocketMax2"
                    id="outOfPocketMax2"
                    placeholder={
                      isNoMaximumOOPDollars(value)
                        ? "No maximum"
                        : "Out of Pocket Max"
                    }
                    value={isNoMaximumOOPDollars(value) ? null : value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                  />
                )}
              />
            </div>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
              <Label htmlFor="flatCopay2">Flat Copay</Label>

              <Controller
                name="flatCopay2"
                control={control}
                render={({ field: { value } }) => (
                  <StyledInputNumber
                    disabled
                    data-testid="flatCopay2"
                    id="flatCopay2"
                    placeholder="Flat copay"
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                  />
                )}
              />
              <EngineProvenanceBanner
                response={order?.secondaryBenefitDetails?.response}
                benefit="FLAT_COPAY"
                testId="orderView-engineProvenance-flatCopay2"
              />
            </div>
            <div className="field flex flex-column col-12 md:col-4 lg:col-3 justify-content-end">
              <Label htmlFor="coinsurancePercent2">Coinsurance Percent</Label>

              <Controller
                name="coinsurancePercent2"
                control={control}
                render={({ field: { value } }) => (
                  <StyledInputNumber
                    disabled
                    suffix="%"
                    data-testid="coinsurancePercent2"
                    id="coinsurancePercent2"
                    placeholder="Coinsurance Percent"
                    value={value}
                  />
                )}
              />
              <EngineProvenanceBanner
                response={order?.secondaryBenefitDetails?.response}
                benefit="COINSURANCE_PERCENT"
                testId="orderView-engineProvenance-coinsurancePercent2"
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default PatientBenefits;
